'use client';

import { c as _c } from "react/compiler-runtime";
import type { IconButtonProps, MenuItemProps, DividerProps, PopperPlacementType } from '@mui/material';
import { Box, ClickAwayListener, Fade, Paper, Popper, Divider, IconButton, MenuItem, MenuList, Stack } from '@mui/material';
import classnames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode, SyntheticEvent } from 'react';
import { Icon } from '@/components/shared/icon';
import { Link } from '@/components/shared/link';
import { Loader } from '@/components/shared/loader';
import { GroupTitle } from './groupTitle';
import { AuthorizationTooltip } from '../tooltips/authorizationTooltip';
type IsDisabledResult = boolean | {
  disabled: boolean;
  tooltip?: string;
};
export interface OptionMenuItemType {
  id: string;
  groupHeader?: boolean;
  divider?: boolean;
  href?: string;
  dividerProps?: DividerProps;
  icon?: ReactNode;
  text?: ReactNode;
  rightContent?: ReactNode;
  nonInteractive?: boolean;
  menuItemProps?: MenuItemProps;
  predicate?: () => boolean;
  isDisabled?: () => IsDisabledResult | Promise<IsDisabledResult>;
  isAuthorized?: boolean;
  isFeatureSupported?: boolean;
  subMenu?: OptionType[];
  disabledTooltip?: string;
}
export type OptionType = string | OptionMenuItemType;
interface OptionsMenuType {
  icon?: ReactNode;
  iconClassName?: string;
  options: OptionType[];
  placement?: PopperPlacementType;
  iconButtonProps?: IconButtonProps;
  'data-testid'?: string;
  onToggle?: (open: boolean) => void;
  onOptionSelected?: (option: OptionType) => void;
  // Drop the open/close fade so the menu mounts and unmounts synchronously. Use
  // when selecting an option shifts the menu's anchor: with the fade, the
  // closing menu would slide to follow the moved anchor before disappearing.
  disableTransition?: boolean;
  Component?: React.ElementType;
  componentProps?: Record<string, unknown>;
  popperClassName?: string;
  // The menu renders where it sits in the DOM, so any ancestor that hides its
  // overflow crops it — which a caller inside a scroll container only sees once
  // the container is short enough for the menu to reach past it. Pass false
  // there to lift the menu out to the body instead.
  disablePortal?: boolean;
}
const InnerMenu = props => {
  const $ = _c(54);
  const {
    onOptionSelected,
    disableTransition,
    closeMenu,
    open,
    options,
    anchorEl,
    placement,
    popperClassName,
    disablePortal: t0
  } = props;
  const disablePortal = t0 === undefined ? true : t0;
  const [subMenuOpen, setSubMenuOpen] = useState();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [disabledStates, setDisabledStates] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Set();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [loadingIds, setLoadingIds] = useState(t2);
  let t3;
  let t4;
  if ($[2] !== open || $[3] !== options) {
    t3 = () => {
      if (!open) {
        return;
      }
      const optionsWithDynamicDisabled = options.filter(_temp);
      if (optionsWithDynamicDisabled.length === 0) {
        return;
      }
      setLoadingIds(new Set(optionsWithDynamicDisabled.map(_temp2)));
      Promise.all(optionsWithDynamicDisabled.map(async option => {
        const raw = await option.isDisabled();
        const resolved = typeof raw === "boolean" ? {
          disabled: raw
        } : raw;
        setDisabledStates(prev => ({
          ...prev,
          [option.id]: resolved
        }));
        setLoadingIds(prev_0 => {
          const next = new Set(prev_0);
          next.delete(option.id);
          return next;
        });
      }));
    };
    t4 = [open, options];
    $[2] = open;
    $[3] = options;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== anchorEl || $[7] !== closeMenu) {
    t5 = event => {
      if (anchorEl?.contains(event.target as HTMLElement)) {
        return;
      }
      setSubMenuOpen(undefined);
      closeMenu();
    };
    $[6] = anchorEl;
    $[7] = closeMenu;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleClose = t5;
  let t6;
  if ($[9] !== closeMenu || $[10] !== onOptionSelected) {
    t6 = option_0 => {
      if (onOptionSelected) {
        onOptionSelected(option_0);
      }
      setSubMenuOpen(undefined);
      closeMenu();
    };
    $[9] = closeMenu;
    $[10] = onOptionSelected;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleSelect = t6;
  let T0;
  let T1;
  let T2;
  let t10;
  let t11;
  let t7;
  let t8;
  let t9;
  if ($[12] !== disabledStates || $[13] !== handleClose || $[14] !== handleSelect || $[15] !== loadingIds || $[16] !== open || $[17] !== options || $[18] !== props || $[19] !== subMenuOpen) {
    const filteredOptions = options.filter(_temp3);
    T2 = Paper;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = {
        boxShadow: "var(--mui-customShadows-lg)"
      };
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    T1 = ClickAwayListener;
    t10 = handleClose;
    T0 = MenuList;
    t7 = open;
    t8 = _temp4;
    t9 = filteredOptions.map((option_1, index) => {
      if (typeof option_1 === "string") {
        return <MenuItem key={option_1} data-testid={option_1} sx={{
          minWidth: "150px"
        }} onClick={() => {
          handleSelect(option_1);
        }}>{option_1}</MenuItem>;
      } else {
        if ("divider" in option_1) {
          const previousOption = filteredOptions[index - 1];
          if (index === 0 || index === filteredOptions.length - 1 || typeof previousOption !== "string" && "divider" in previousOption) {
            return null;
          }
          if (!option_1.divider) {
            return null;
          }
          return <Divider key={option_1.id} {...option_1.dividerProps} className="my-[8px]" />;
        } else {
          if ("groupHeader" in option_1) {
            return <GroupTitle key={option_1.id} title={option_1.text} />;
          } else {
            if (option_1.nonInteractive) {
              return <Stack key={option_1.id} data-testid={option_1.id} onClick={_temp5} sx={{
                padding: "6px 16px",
                cursor: "default"
              }}><Option icon={option_1.icon} text={option_1.text} rightContent={option_1.rightContent} /></Stack>;
            } else {
              return <SubMenuInnerOption key={option_1.id} option={option_1} subMenuOpen={subMenuOpen} index={index} setSubMenuOpen={setSubMenuOpen} handleSelect={handleSelect} props={props} dynamicDisabledState={disabledStates[option_1.id]} isLoadingDisabled={loadingIds.has(option_1.id)} />;
            }
          }
        }
      }
    });
    $[12] = disabledStates;
    $[13] = handleClose;
    $[14] = handleSelect;
    $[15] = loadingIds;
    $[16] = open;
    $[17] = options;
    $[18] = props;
    $[19] = subMenuOpen;
    $[20] = T0;
    $[21] = T1;
    $[22] = T2;
    $[23] = t10;
    $[24] = t11;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    T0 = $[20];
    T1 = $[21];
    T2 = $[22];
    t10 = $[23];
    t11 = $[24];
    t7 = $[25];
    t8 = $[26];
    t9 = $[27];
  }
  let t12;
  if ($[29] !== T0 || $[30] !== t7 || $[31] !== t8 || $[32] !== t9) {
    t12 = <T0 autoFocusItem={t7} onClick={t8}>{t9}</T0>;
    $[29] = T0;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== T1 || $[35] !== t10 || $[36] !== t12) {
    t13 = <T1 onClickAway={t10}>{t12}</T1>;
    $[34] = T1;
    $[35] = t10;
    $[36] = t12;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== T2 || $[39] !== t11 || $[40] !== t13) {
    t14 = <T2 sx={t11}>{t13}</T2>;
    $[38] = T2;
    $[39] = t11;
    $[40] = t13;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  const menuContent = t14;
  const t15 = !disableTransition;
  let t16;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      zIndex: 5
    };
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== disableTransition || $[44] !== menuContent) {
    t17 = disableTransition ? menuContent : t18 => {
      const {
        TransitionProps
      } = t18;
      return <Fade {...TransitionProps}>{menuContent}</Fade>;
    };
    $[43] = disableTransition;
    $[44] = menuContent;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== anchorEl || $[47] !== disablePortal || $[48] !== open || $[49] !== placement || $[50] !== popperClassName || $[51] !== t15 || $[52] !== t17) {
    t18 = <Popper open={open} anchorEl={anchorEl} placement={placement} transition={t15} disablePortal={disablePortal} className={popperClassName} sx={t16}>{t17}</Popper>;
    $[46] = anchorEl;
    $[47] = disablePortal;
    $[48] = open;
    $[49] = placement;
    $[50] = popperClassName;
    $[51] = t15;
    $[52] = t17;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  return t18;
};
const SubMenuInnerOption = t0 => {
  const $ = _c(43);
  const {
    option,
    subMenuOpen,
    index,
    setSubMenuOpen,
    handleSelect,
    props,
    dynamicDisabledState,
    isLoadingDisabled
  } = t0;
  const [anchorEl, setAnchorEl] = useState(null);
  const isBlocked = option.isAuthorized === false || option.isFeatureSupported === false;
  const isDisabled = option.menuItemProps?.disabled || isLoadingDisabled || dynamicDisabledState?.disabled || isBlocked;
  const tooltipTitle = isDisabled ? dynamicDisabledState?.tooltip ?? option.disabledTooltip : undefined;
  let t1;
  if ($[0] !== option.href) {
    t1 = option.href ? {
      component: Link,
      href: option.href
    } : {};
    $[0] = option.href;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const linkProps = t1;
  let t2;
  if ($[2] !== index || $[3] !== option.menuItemProps || $[4] !== setSubMenuOpen) {
    t2 = e => {
      setSubMenuOpen(index);
      option.menuItemProps?.onMouseEnter?.(e);
    };
    $[2] = index;
    $[3] = option.menuItemProps;
    $[4] = setSubMenuOpen;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== option.menuItemProps || $[7] !== setSubMenuOpen) {
    t3 = e_0 => {
      setSubMenuOpen(undefined);
      option.menuItemProps?.onMouseLeave?.(e_0);
    };
    $[6] = option.menuItemProps;
    $[7] = setSubMenuOpen;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== handleSelect || $[10] !== index || $[11] !== option || $[12] !== setSubMenuOpen) {
    t4 = e_1 => {
      if (option.subMenu) {
        setSubMenuOpen(index);
      } else {
        handleSelect(option);
      }
      option.menuItemProps?.onClick?.(e_1);
    };
    $[9] = handleSelect;
    $[10] = index;
    $[11] = option;
    $[12] = setSubMenuOpen;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const t5 = !!option.subMenu;
  let t6;
  if ($[14] !== isLoadingDisabled || $[15] !== option.icon || $[16] !== option.rightContent || $[17] !== option.text || $[18] !== t5) {
    t6 = <Option icon={option.icon} text={option.text} rightContent={option.rightContent} isSubMenu={t5} isLoadingDisabled={isLoadingDisabled} />;
    $[14] = isLoadingDisabled;
    $[15] = option.icon;
    $[16] = option.rightContent;
    $[17] = option.text;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== anchorEl || $[21] !== index || $[22] !== option.subMenu || $[23] !== props || $[24] !== setSubMenuOpen || $[25] !== subMenuOpen) {
    t7 = option.subMenu && <InnerMenu {...props} anchorEl={anchorEl} options={option.subMenu} popperClassName={undefined} disablePortal={true} placement="right-start" open={subMenuOpen === index} closeMenu={() => {
      setSubMenuOpen(undefined);
      props.closeMenu();
    }} />;
    $[20] = anchorEl;
    $[21] = index;
    $[22] = option.subMenu;
    $[23] = props;
    $[24] = setSubMenuOpen;
    $[25] = subMenuOpen;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== isDisabled || $[28] !== linkProps || $[29] !== option.id || $[30] !== option.menuItemProps || $[31] !== t2 || $[32] !== t3 || $[33] !== t4 || $[34] !== t6 || $[35] !== t7) {
    t8 = <MenuItem {...option.menuItemProps} {...linkProps} disabled={isDisabled} ref={setAnchorEl} onMouseEnter={t2} onMouseLeave={t3} onClick={t4} data-testid={option.id}>{t6}{t7}</MenuItem>;
    $[27] = isDisabled;
    $[28] = linkProps;
    $[29] = option.id;
    $[30] = option.menuItemProps;
    $[31] = t2;
    $[32] = t3;
    $[33] = t4;
    $[34] = t6;
    $[35] = t7;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== option.id || $[38] !== option.isAuthorized || $[39] !== option.isFeatureSupported || $[40] !== t8 || $[41] !== tooltipTitle) {
    t9 = <AuthorizationTooltip isAuthorized={option.isAuthorized} isFeatureSupported={option.isFeatureSupported} title={tooltipTitle} key={option.id}>{t8}</AuthorizationTooltip>;
    $[37] = option.id;
    $[38] = option.isAuthorized;
    $[39] = option.isFeatureSupported;
    $[40] = t8;
    $[41] = tooltipTitle;
    $[42] = t9;
  } else {
    t9 = $[42];
  }
  return t9;
};
const OptionMenu = props => {
  const $ = _c(28);
  const {
    icon: t0,
    iconClassName,
    iconButtonProps,
    onToggle,
    Component,
    componentProps,
    placement: t1,
    "data-testid": t2
  } = props;
  const icon = t0 === undefined ? "material-symbols-more-horiz" : t0;
  const placement = t1 === undefined ? "bottom-end" : t1;
  const dataTestId = t2 === undefined ? "option-menu-button" : t2;
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  let t3;
  if ($[0] !== onToggle || $[1] !== open) {
    t3 = event => {
      event.stopPropagation();
      setOpen(_temp6);
      onToggle?.(!open);
    };
    $[0] = onToggle;
    $[1] = open;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handleToggle = t3;
  if (props.options.length === 0 || props.options.every(_temp7)) {
    return null;
  }
  const ButtonComponent = Component as React.ComponentType<Record<string, unknown> & {
    ref?: React.Ref<HTMLElement>;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
  }>;
  let t4;
  if ($[3] !== ButtonComponent || $[4] !== Component || $[5] !== componentProps || $[6] !== dataTestId || $[7] !== handleToggle) {
    t4 = Component && <ButtonComponent {...componentProps} data-testid={dataTestId} ref={setAnchorEl} onClick={handleToggle} />;
    $[3] = ButtonComponent;
    $[4] = Component;
    $[5] = componentProps;
    $[6] = dataTestId;
    $[7] = handleToggle;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== Component || $[10] !== dataTestId || $[11] !== handleToggle || $[12] !== icon || $[13] !== iconButtonProps || $[14] !== iconClassName) {
    t5 = !Component && <IconButton disableRipple={true} ref={setAnchorEl} data-testid={dataTestId} onClick={handleToggle} {...iconButtonProps}>{typeof icon === "string" ? <i className={classnames(icon, "text-(--mui-palette-text-primary)", iconClassName)} /> : icon}</IconButton>;
    $[9] = Component;
    $[10] = dataTestId;
    $[11] = handleToggle;
    $[12] = icon;
    $[13] = iconButtonProps;
    $[14] = iconClassName;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== onToggle) {
    t6 = () => {
      setOpen(false);
      onToggle?.(false);
    };
    $[16] = onToggle;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== anchorEl || $[19] !== open || $[20] !== placement || $[21] !== props || $[22] !== t6) {
    t7 = <InnerMenu {...props} placement={placement} open={open} closeMenu={t6} anchorEl={anchorEl} />;
    $[18] = anchorEl;
    $[19] = open;
    $[20] = placement;
    $[21] = props;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t4 || $[25] !== t5 || $[26] !== t7) {
    t8 = <>{t4}{t5}{t7}</>;
    $[24] = t4;
    $[25] = t5;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  return t8;
};
export const menuItemStyles = (isSelected: boolean) => {
  return {
    height: '36px',
    padding: '4px 16px 4px 16px',
    borderRadius: 0,
    ...(isSelected ? {
      backgroundColor: 'var(--mui-palette-primary-main)',
      color: 'var(--mui-palette-background-default)'
    } : {}),
    '&:hover': {
      backgroundColor: isSelected ? 'var(--mui-palette-primary-main)' : 'var(--mui-palette-primary-dark)'
    }
  };
};
export { OptionMenu };
const Option = t0 => {
  const $ = _c(17);
  const {
    icon,
    text,
    rightContent,
    isSubMenu,
    isLoadingDisabled
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      gap: "4px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      alignItems: "center",
      gap: "8px",
      marginRight: "8px"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== icon) {
    t3 = (typeof icon === "string" ? <Icon iconClass={icon} color="currentColor" /> : icon) || null;
    $[2] = icon;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3 || $[5] !== text) {
    t4 = <Stack direction="row" sx={t2}>{t3}{text}</Stack>;
    $[4] = t3;
    $[5] = text;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== isLoadingDisabled) {
    t5 = isLoadingDisabled && <Loader size="small" color="inherit" />;
    $[7] = isLoadingDisabled;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== isLoadingDisabled || $[10] !== isSubMenu) {
    t6 = isSubMenu && !isLoadingDisabled && <Box component="span" sx={{
      display: "inline-flex",
      "& > span": {
        height: "20px",
        padding: "0px",
        width: "20px"
      }
    }}><Icon iconClass="material-symbols-chevron-right-rounded" color="currentColor" /></Box>;
    $[9] = isLoadingDisabled;
    $[10] = isSubMenu;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== rightContent || $[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = <Stack direction="row" sx={t1}>{t4}{rightContent}{t5}{t6}</Stack>;
    $[12] = rightContent;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};
function _temp(o) {
  return typeof o === "object" && "isDisabled" in o && typeof o.isDisabled === "function";
}
function _temp2(o_0) {
  return o_0.id;
}
function _temp3(o_1) {
  return typeof o_1 === "object" && "predicate" in o_1 ? o_1.predicate?.() : true;
}
function _temp4(e) {
  return e.stopPropagation();
}
function _temp5(e_0) {
  return e_0.stopPropagation();
}
function _temp6(prevOpen) {
  return !prevOpen;
}
function _temp7(obj) {
  return typeof obj === "object" && ("predicate" in obj && !obj.predicate?.() || obj.divider);
}